import { type JSX, Show, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { floatingMount } from './floatingLayer';

const escStack: Array<() => void> = [];

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchor: HTMLElement | undefined;
  align?: 'start' | 'center' | 'end';
  gap?: number;
  zIndex?: number;
  children: JSX.Element;
}

export function Popover(props: PopoverProps): JSX.Element {
  const [pos, setPos] = createSignal<{ top: number; left: number; flip: boolean }>({
    top: 0,
    left: 0,
    flip: false,
  });
  let panelRef: HTMLDivElement | undefined;

  const reposition = () => {
    const anchor = props.anchor;
    if (!anchor || !panelRef) return;
    const r = anchor.getBoundingClientRect();
    const pw = panelRef.offsetWidth;
    const ph = panelRef.offsetHeight;
    const gap = props.gap ?? 6;
    const align = props.align ?? 'start';

    let left =
      align === 'center'
        ? r.left + r.width / 2 - pw / 2
        : align === 'end'
          ? r.right - pw
          : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));

    const roomBelow = window.innerHeight - r.bottom - gap;
    const roomAbove = r.top - gap;
    const flip = roomBelow < ph && roomAbove > roomBelow;
    const top = flip ? Math.max(8, r.top - gap - ph) : r.bottom + gap;
    setPos({ top, left, flip });
  };

  createEffect(() => {
    if (!props.open) return;
    const raf = requestAnimationFrame(reposition);
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    onCleanup(() => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    });
  });

  createEffect(() => {
    if (!props.open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef?.contains(t)) return;
      if (props.anchor?.contains(t)) return;
      props.onClose();
    };
    const raf = requestAnimationFrame(() =>
      window.addEventListener('pointerdown', onDown, true),
    );
    onCleanup(() => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointerdown', onDown, true);
    });
  });

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (escStack[escStack.length - 1] !== props.onClose) return;
    e.preventDefault();
    e.stopPropagation();
    props.onClose();
  };
  createEffect(() => {
    if (!props.open) return;
    escStack.push(props.onClose);
    window.addEventListener('keydown', onKey, true);
    onCleanup(() => {
      const i = escStack.lastIndexOf(props.onClose);
      if (i >= 0) escStack.splice(i, 1);
      window.removeEventListener('keydown', onKey, true);
    });
  });

  return (
    <Show when={props.open}>
      <Portal mount={floatingMount(props.anchor)}>
        <div
          ref={panelRef}
          data-floating-layer=""
          class="fixed"
          style={{
            'z-index': String(props.zIndex ?? 1300),
            top: `${pos().top}px`,
            left: `${pos().left}px`,
          }}
        >
          {props.children}
        </div>
      </Portal>
    </Show>
  );
}
